// PauseController — single source of truth for in-match pause/resume,
// driven by the Page Visibility API and explicit calls. CrazyGames
// requires that gameplay pauses when the player tabs away or minimises
// the window; we also use this hook to fade audio and stop the local
// simulation loop.
//
// In local mode the LocalMatchHost's accumulator is paused (it stops
// advancing while paused). In network mode the simulation lives on
// the server, so pause only affects local presentation (audio mute,
// FX freeze) — the game world keeps progressing.
//
// The controller is intentionally NOT routed through MatchState
// (Rule 1: simulation is server-authoritative; client pause is a
// presentation concern). Auto-pause fires only when a match is
// actually in progress; the menu / EOM modal never trip the listener.

import type { LocalMatchHost } from '@signalclash/shared';

import type { AudioBus } from '../audio/AudioBus.js';

export type PauseReason = 'visibility' | 'manual';

type Listener = (paused: boolean, reason: PauseReason | null) => void;

export class PauseController {
  private paused = false;
  private inMatch = false;
  private localHost: LocalMatchHost | null = null;
  private audio: AudioBus | null = null;
  private isNetwork = false;
  private listeners: Listener[] = [];
  private vizListener: (() => void) | null = null;

  attach(opts: { host: LocalMatchHost; audio: AudioBus; isNetwork: boolean }): void {
    this.detach();
    this.localHost = opts.host;
    this.audio = opts.audio;
    this.isNetwork = opts.isNetwork;
    this.inMatch = true;
    this.paused = false;
    if (typeof document !== 'undefined') {
      this.vizListener = (): void => this.onVisibilityChange();
      document.addEventListener('visibilitychange', this.vizListener);
    }
  }

  detach(): void {
    if (typeof document !== 'undefined' && this.vizListener !== null) {
      document.removeEventListener('visibilitychange', this.vizListener);
    }
    this.vizListener = null;
    this.inMatch = false;
    if (this.paused) this.setPaused(false, null);
    this.localHost = null;
    this.audio = null;
  }

  isPaused(): boolean {
    return this.paused;
  }

  // Toggle pause from a user gesture (e.g. ESC pause button). The
  // CrazyGames SDK also calls this from its own pause notifications.
  pause(reason: PauseReason = 'manual'): void {
    if (!this.inMatch || this.paused) return;
    this.setPaused(true, reason);
  }

  resume(): void {
    if (!this.paused) return;
    this.setPaused(false, null);
  }

  subscribe(listener: Listener): () => void {
    this.listeners.push(listener);
    return () => {
      const i = this.listeners.indexOf(listener);
      if (i !== -1) this.listeners.splice(i, 1);
    };
  }

  private onVisibilityChange(): void {
    if (typeof document === 'undefined') return;
    if (document.hidden) {
      // Tabbed away / minimised. Auto-pause if a match is active.
      if (this.inMatch && !this.paused) this.setPaused(true, 'visibility');
    } else {
      // Tabbed back. Only auto-resume if we paused for visibility.
      // Manual pauses stay paused until the user clicks Resume.
      if (this.paused && this.currentReason === 'visibility') {
        this.setPaused(false, null);
      }
    }
  }

  private currentReason: PauseReason | null = null;

  private setPaused(paused: boolean, reason: PauseReason | null): void {
    this.paused = paused;
    this.currentReason = reason;
    // Local-host pause: the host's update() accumulator stops advancing
    // when we mark the run as paused. We achieve this by simply not
    // calling .update() from the client loop while paused (see ClientLoop).
    // Audio: mute on pause; restore on resume. Music doesn't restart from
    // zero — the bus handles fade.
    if (paused) {
      this.audio?.suspendForPause();
    } else {
      this.audio?.resumeFromPause();
    }
    for (const l of this.listeners) {
      try {
        l(paused, reason);
      } catch (err) {
        // Same containment rationale as the connection snapshot fan-out:
        // one bad subscriber must not stop the others, and must not
        // climb back into the visibility-change handler.
        // eslint-disable-next-line no-console
        console.warn('[pause listener] threw', err);
      }
    }
  }
}
